import type { SeatCandidate } from "./seat-identity";
import type { SeatLayoutDefinition } from "./seat-layouts";

export type SeatDuelCriterion = "aisle" | "center" | "price";
export const seatDuelLabels: Record<SeatDuelCriterion, string> = {
  aisle: "통로",
  center: "가운데",
  price: "가격",
};

/** Facility transcription only. This does not establish event configuration or sightlines. */
export function seatDuelFact(
  layout: SeatLayoutDefinition,
  candidate: SeatCandidate | null,
) {
  if (
    !candidate ||
    layout.precision !== "seat" ||
    candidate.venueId !== layout.venueId ||
    candidate.spaceId !== layout.spaceId ||
    candidate.layoutVersionId !== layout.layoutVersionId
  )
    return null;
  for (const floor of layout.floors)
    for (const section of floor.sections) {
      if (candidate.sectionCode !== section.label) continue;
      const row = section.rows?.find(
        (item) => item.label === candidate.rowLabel,
      );
      const index = row?.seats.indexOf(candidate.seatNumber) ?? -1;
      if (!row || index < 0) return null;
      return {
        floor: floor.label,
        section: section.label,
        row: row.label,
        rowIndex: (section.rows?.indexOf(row) ?? 0) + 1,
        rowCount: section.rows?.length ?? 0,
        // An absent aisle annotation is unknown, not evidence of no aisle.
        aisle:
          row.aisleAfter === undefined
            ? null
            : row.aisleAfter.some(
                (seat) =>
                  seat === candidate.seatNumber ||
                  row.seats[row.seats.indexOf(seat) + 1] ===
                    candidate.seatNumber,
              ),
        centerOffset: Math.abs(index - (row.seats.length - 1) / 2),
        price: candidate.expectedPrice,
      };
    }
  return null;
}

export function compareSeatDuel(
  layout: SeatLayoutDefinition,
  a: SeatCandidate | null,
  b: SeatCandidate | null,
  criterion: SeatDuelCriterion,
) {
  const fa = seatDuelFact(layout, a),
    fb = seatDuelFact(layout, b);
  const same =
    a &&
    b &&
    a.sectionCode === b.sectionCode &&
    a.rowLabel === b.rowLabel &&
    a.seatNumber === b.seatNumber;
  if (!fa || !fb || same)
    return {
      valid: false,
      a: fa,
      b: fb,
      winner: null,
      reason: "같은 시설 배치에 있는 서로 다른 두 좌석을 넣어 주세요.",
    } as const;
  let winner: "A" | "B" | null = null;
  let reason: string;
  if (criterion === "aisle") {
    if (fa.aisle === null || fb.aisle === null)
      reason =
        "통로 전사 정보가 없는 후보가 있어요. 모르는 조건으로 순서를 정하지 않아요.";
    else if (fa.aisle === fb.aisle)
      reason = "두 후보의 전사된 통로 조건이 같아요.";
    else {
      winner = fa.aisle ? "A" : "B";
      reason = `${winner}는 시설 전사에서 통로 옆으로 확인돼요.`;
    }
  } else if (criterion === "center") {
    if (a?.sectionCode !== b?.sectionCode)
      reason = "서로 다른 구역의 가운데를 같은 기준으로 비교하지 않아요.";
    else if (fa.centerOffset === fb.centerOffset)
      reason = "각 열의 좌석 순서상 가운데까지 거리가 같아요.";
    else {
      winner = fa.centerOffset < fb.centerOffset ? "A" : "B";
      reason = `${winner}가 해당 열의 좌석 순서상 가운데에 ${Math.abs(fa.centerOffset - fb.centerOffset)}석 더 가까워요. 무대 중심이나 시야 비교는 아니에요.`;
    }
  } else if (fa.price === null || fb.price === null)
    reason = "두 예상 가격을 직접 넣어야 비교할 수 있어요.";
  else if (fa.price === fb.price) reason = "직접 넣은 예상 가격이 같아요.";
  else {
    winner = fa.price < fb.price ? "A" : "B";
    reason = `${winner}가 직접 넣은 예상 가격에서 ${Math.abs(fa.price - fb.price).toLocaleString("ko-KR")}원 적어요.`;
  }
  return { valid: true, a: fa, b: fb, winner, reason };
}
